/** @jsxImportSource theme-ui */
import { useBreakpointIndex } from '@theme-ui/match-media';
import classNames from 'classnames';
import { Formik } from 'formik';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DropdownItemProps, Input, List } from 'semantic-ui-react';
import * as Yup from 'yup';

import * as dto from 'client/dto';
import { isMobile } from 'utils/responsive';
import { nameof } from 'utils/typeHelpers';

import { FlexGroup } from 'components/_common/FlexGroup';
import { Typography } from 'components/_common/Typography';
import { Stone90Link } from 'components/_controls/Misc/Stone90Link';

import { updateUserNotifications } from 'user/user.saga';
import * as UserSelectors from 'user/user.selectors';

import BdFormLabel from '../_common/BdFormLabel';
import SettingsSubmitButton from '../_common/SettingsSubmitButton';
import {
  FlexFormRow,
  FormFieldGroup,
  InputErrorMessage,
  PageHeading,
} from '../CustomSettings/CustomSettingsForm';
import { FormikSemanticDropDown } from '../FormikControls/FormikSemanticDropDown';
import { FormikSemanticToggleButton } from '../FormikControls/FormikSemanticToggleButton';

type AccountDetailsFM = {
  email: string;
};
type NotificationSettingsFM = {
  isDailyBriefEmailEnabled: boolean;
  isWeeklyBriefEmailEnabled: boolean;
  isNewActivityEmailEnabled: boolean;
  dayOfWeekIfWeekly: dto.DayOfWeek;
};
const dayOfWeekOptions: DropdownItemProps[] = [
  { text: 'Sunday', value: dto.DayOfWeek.Sunday },
  { text: 'Monday', value: dto.DayOfWeek.Monday },
  { text: 'Tuesday', value: dto.DayOfWeek.Tuesday },
  { text: 'Wednesday', value: dto.DayOfWeek.Wednesday },
  { text: 'Thursday', value: dto.DayOfWeek.Thursday },
  { text: 'Friday', value: dto.DayOfWeek.Friday },
  { text: 'Saturday', value: dto.DayOfWeek.Saturday },
];

const NotificationSettings: React.FC = () => {
  const dispatch = useDispatch();

  const userProfile = useSelector(UserSelectors.userProfileSelector);

  const isMobileDevice = isMobile(useBreakpointIndex());

  const handleFormSubmit = (settings: dto.UserNotificationsModel) => {
    dispatch(updateUserNotifications(settings));
  };

  return (
    <div sx={{ marginTop: '30px' }}>
      <PageHeading>Your Email</PageHeading>
      <Formik<AccountDetailsFM>
        enableReinitialize
        initialValues={{
          email: userProfile?.notificationsSetting?.email,
        }}
        validationSchema={Yup.object().shape<AccountDetailsFM>({
          email: Yup.string()
            .email('Please provide a valid email address')
            .required('An email address is required')
            .max(256, 'Email cannot be longer than 256 characters'),
        })}
        onSubmit={values => {
          handleFormSubmit({ ...userProfile.notificationsSetting, ...values });
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          dirty,
          isValid,
        }) => {
          return (
            <form
              onSubmit={handleSubmit}
              className={dirty ? 'was-validated' : ''}
              sx={{ mb: '20px' }}
            >
              <FlexFormRow>
                <FormFieldGroup>
                  <BdFormLabel>Email Address</BdFormLabel>
                  <Input
                    fluid
                    placeholder="email@example.com"
                    name={nameof<AccountDetailsFM>('email')}
                    error={touched.email && !!errors.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    className={`form-control ${touched.email && errors.email ? 'is-invalid' : ''}`}
                  />
                  <InputErrorMessage>{errors.email}</InputErrorMessage>
                </FormFieldGroup>
                <div sx={{ flex: '1 0 320px' }} />
              </FlexFormRow>
              <SettingsSubmitButton
                disabled={!isValid || !dirty || isSubmitting}
                text="Update Email Address"
              />
            </form>
          );
        }}
      </Formik>

      <PageHeading>Notifications Preferences</PageHeading>
      <Formik<NotificationSettingsFM>
        enableReinitialize
        initialValues={{
          isDailyBriefEmailEnabled: userProfile?.notificationsSetting?.isDailyBriefEmailEnabled,
          isWeeklyBriefEmailEnabled: userProfile?.notificationsSetting?.isWeeklyBriefEmailEnabled,
          isNewActivityEmailEnabled: userProfile?.notificationsSetting?.isNewActivityEmailEnabled,
          dayOfWeekIfWeekly:
            userProfile?.notificationsSetting?.weeklyEmailDeliverySettings?.deliveryDay,
        }}
        onSubmit={values => {
          handleFormSubmit({
            ...userProfile.notificationsSetting,
            isDailyBriefEmailEnabled: values.isDailyBriefEmailEnabled,
            isWeeklyBriefEmailEnabled: values.isWeeklyBriefEmailEnabled,
            isNewActivityEmailEnabled: values.isNewActivityEmailEnabled,
            weeklyEmailDeliverySettings: values.isWeeklyBriefEmailEnabled
              ? {
                  deliveryDay: values.dayOfWeekIfWeekly,
                  deliveryTime: '00:00:00',
                }
              : null,
          });
        }}
      >
        {({ values, handleSubmit, isSubmitting, isValid, dirty }) => (
          <form sx={{ mt: '2em' }} onSubmit={handleSubmit}>
            <List
              className={classNames({
                xunpadded: 1,
                'bolder-separators': 1,
                'dont-pad-first': isMobileDevice,
              })}
              sx={{ '&.ui.list': { mb: '20px' } }}
            >
              <List.Item>
                <Typography variant="h6" color="stone90">
                  Disease activity briefs
                </Typography>
                <Typography variant="body2" color="stone90">
                  Receive summaries of recent disease activity that are relevant to your location(s)
                  and <Stone90Link to="/settings/customsettings">Insights Settings</Stone90Link>.
                </Typography>
                <List>
                  <List.Item>
                    <FlexGroup
                      alignItems="center"
                      suffix={
                        <FormikSemanticToggleButton
                          name={nameof<NotificationSettingsFM>('isDailyBriefEmailEnabled')}
                        />
                      }
                    >
                      <Typography variant="subtitle2" color="stone90">
                        Daily brief
                      </Typography>
                      <Typography variant="body2" color="stone90">
                        Receive a daily summary of disease activity in the last 24 hours.
                      </Typography>
                    </FlexGroup>
                  </List.Item>
                  <List.Item>
                    <FlexGroup
                      alignItems="center"
                      suffix={
                        <FormikSemanticToggleButton
                          name={nameof<NotificationSettingsFM>('isWeeklyBriefEmailEnabled')}
                        />
                      }
                    >
                      <Typography variant="subtitle2" color="stone90">
                        Weekly brief
                      </Typography>
                      <Typography variant="body2" color="stone90">
                        Receive a weekly summary of disease activity in the last 7 days.
                      </Typography>
                    </FlexGroup>
                    <FlexFormRow>
                      <FormFieldGroup>
                        <Typography
                          variant="body2"
                          color={values.isWeeklyBriefEmailEnabled ? 'stone90' : 'stone50'}
                          sx={{ mt: '24px', mb: '6px' }}
                        >
                          Send weekly summaries on:
                        </Typography>
                        <FormikSemanticDropDown
                          fluid
                          name={nameof<NotificationSettingsFM>('dayOfWeekIfWeekly')}
                          options={dayOfWeekOptions}
                          disabled={!values.isWeeklyBriefEmailEnabled}
                        />
                      </FormFieldGroup>
                      <div sx={{ flex: '1 0 320px' }} />
                    </FlexFormRow>
                    {new Date().getTimezoneOffset() <= 0 && (
                      <Typography
                        variant="caption"
                        color="stone90"
                        sx={{ mt: '10px', maxWidth: '600px' }}
                      >
                        We send briefs at the end of the day (GMT). Based on your browser’s
                        timezone, you’ll receive your weekly brief on{' '}
                        <span sx={{ fontWeight: 600 }}>
                          {
                            dayOfWeekOptions[
                              (values.dayOfWeekIfWeekly + 1) % dayOfWeekOptions.length
                            ]?.text
                          }{' '}
                          morning
                        </span>
                        .
                      </Typography>
                    )}
                  </List.Item>
                </List>
              </List.Item>
              <List.Item>
                <Typography variant="h6" color="stone90">
                  BlueDot will always notify you about emerging and breaking news.
                </Typography>
              </List.Item>
            </List>
            <SettingsSubmitButton
              disabled={!isValid || !dirty || isSubmitting}
              text="Save Notification Preferences"
            />
          </form>
        )}
      </Formik>
    </div>
  );
};
export default NotificationSettings;
